import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, MapPin, Briefcase, Clock, IndianRupee, ArrowRight, MessageCircle, Upload, X, CheckCircle2 } from 'lucide-react';
import { getCareers, submitApplication } from '../lib/api';
import { BRAND } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import Seo from '../components/Seo';

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    getCareers().then(list => setJobs((list || []).filter(j => j.is_open))).catch(() => {});
  }, []);

  return (
    <div className="pt-24 pb-20">
      <Seo title="Careers — Join the Zayka Sphere Team" path="/careers" description="Work at Zayka Sphere in Rajajipuram, Lucknow. View open roles for chefs, delivery riders & counter staff, and apply online with your CV in minutes." />
      {/* HERO */}
      <section className="relative overflow-hidden ember-bg">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-[28rem] h-[28rem] bg-orange-500/15 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em] mb-5">
            <Flame className="w-3.5 h-3.5" /> Careers
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">Join The <span className="text-orange-500 fire-glow">Zayka Sphere</span> Crew</h1>
          <p className="mt-5 text-white/70 text-lg max-w-2xl mx-auto">Love food and fast-paced energy? Come build the taste ka asli global station with us in Lucknow.</p>
        </div>
      </section>

      {/* OPENINGS */}
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl mb-8">Current <span className="text-orange-500">Openings</span></h2>
          {jobs.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-neutral-900 p-10 text-center">
              <p className="text-white/60">No open positions right now. Check back soon — or say hi on WhatsApp and we'll keep you in mind!</p>
              <a href={BRAND.whatsapp} target="_blank" rel="noreferrer"><Button className="mt-5 h-11 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><MessageCircle className="w-4 h-4 mr-2" /> Message Us</Button></a>
            </div>
          ) : (
            <div className="space-y-4 tilt-scene">
              {jobs.map((job, i) => (
                <motion.div
                  key={job.id}
                  data-testid={`career-${job.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                >
                  <div className="food-card relative overflow-hidden bg-neutral-900 border border-white/10 rounded-2xl p-6 will-change-transform">
                    <span className="tilt-glare" />
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-[240px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bebas text-2xl tracking-wide">{job.title}</h3>
                          {job.department && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-orange-500/40 text-orange-300">{job.department}</span>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/55">
                          {job.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-orange-400" /> {job.location}</span>}
                          {job.type && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-orange-400" /> {job.type}</span>}
                          {job.salary && <span className="inline-flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5 text-orange-400" /> {job.salary}</span>}
                        </div>
                        {job.description && <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-2xl">{job.description}</p>}
                      </div>
                      <Button data-testid={`apply-${job.id}`} onClick={() => setApplyJob(job)} className="h-11 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Apply Now <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-10 rounded-2xl border border-orange-500/20 bg-orange-500/5 p-6 flex items-center gap-4">
            <Briefcase className="w-8 h-8 text-orange-400 shrink-0" />
            <p className="text-sm text-white/70">Don't see the right role? We're always looking for passionate people. Reach out and tell us how you'd add flavour to the team.</p>
          </div>
        </div>
      </section>

      {applyJob && <ApplyDialog job={applyJob} onClose={() => setApplyJob(null)} />}
    </div>
  );
}

function ApplyDialog({ job, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast({ title: 'Missing details', description: 'Please add your name and email.', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('position', job.title);
      fd.append('message', form.message);
      if (cv) fd.append('cv', cv);
      await submitApplication(fd);
      setDone(true);
    } catch (err) {
      toast({ title: 'Could not submit', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-orange-500/30 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl tracking-wide">Apply — {job.title}</DialogTitle>
        <DialogDescription className="text-white/50">Fill in your details and attach a CV. Our team will review your application.</DialogDescription></DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="apply-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <h3 className="mt-4 font-bebas text-2xl">Application Sent!</h3>
            <p className="mt-2 text-white/60 text-sm">Thanks for applying to Zayka Sphere. Our team will review your details and reach out if there's a match.</p>
            <Button onClick={onClose} className="mt-5 h-11 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Done</Button>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <Input data-testid="apply-name" placeholder="Full name" value={form.name} onChange={e => set('name', e.target.value)} className="h-11 bg-black/50 border-white/10" />
            <Input data-testid="apply-email" type="email" placeholder="Email" value={form.email} onChange={e => set('email', e.target.value)} className="h-11 bg-black/50 border-white/10" />
            <Input data-testid="apply-phone" placeholder="Phone / WhatsApp" value={form.phone} onChange={e => set('phone', e.target.value)} className="h-11 bg-black/50 border-white/10" />
            <Textarea data-testid="apply-message" placeholder="Why are you a great fit? (optional)" value={form.message} onChange={e => set('message', e.target.value)} className="bg-black/50 border-white/10" />
            <label className="flex items-center gap-3 rounded-xl border border-dashed border-white/20 bg-black/30 p-3 cursor-pointer hover:border-orange-500/50 transition">
              <Upload className="w-5 h-5 text-orange-400 shrink-0" />
              <span className="text-sm text-white/70 truncate">{cv ? cv.name : 'Upload CV / Resume (PDF, DOC, image — optional, max 8MB)'}</span>
              <input data-testid="apply-cv" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" className="hidden" onChange={e => setCv(e.target.files?.[0] || null)} />
            </label>
            {cv && <button onClick={() => setCv(null)} className="text-xs text-white/40 hover:text-red-400 inline-flex items-center gap-1"><X className="w-3 h-3" /> Remove file</button>}
            <Button data-testid="apply-submit" onClick={submit} disabled={busy} className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">{busy ? 'Submitting…' : 'Submit Application'}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
